import { useEffect, useState, useCallback, useRef } from 'react';
import styles from './SelectCameraAnglesModal.module.scss';
import { ModalArea } from '../../../../common/ModalButtonArea';
import Checkbox from '../../../../common/Checkbox';
import Button from '../../../../components/Button/Button';
import { Loader } from '../../../../components/Loader/Loader';
import { warningToast } from '../../../../helpers/toastHelper';
import { getCameraDataFromApi } from '../../../../api/configuratorApi';
import { useDispatch } from 'react-redux';

const buildInitialSelections = (templates, existing = {}) => {
  const init = {};
  templates.forEach((t) => {
    const saved = existing[t.id]?.presetIds;
    init[t.id] = saved ? new Set(saved) : new Set();
  });
  return init;
};

export default function SelectCameraAnglesModal({ show, onClose, onSave, templates = [], initialSelections = {} }) {
  const [activeTemplateId, setActiveTemplateId] = useState(null);
  const [selections, setSelections] = useState({});
  // presetsMap: { [templateId]: preset[] }
  const [presetsMap, setPresetsMap] = useState({});
  const [loadingId, setLoadingId] = useState(null);
  const [resetKey, setResetKey] = useState(0);
  const dispatch = useDispatch();
  const fetchedTemplates = useRef(new Set());

  useEffect(() => {
    if (show && templates.length > 0) {
      const firstId = templates[0]?.id;
      setActiveTemplateId(firstId);
      setSelections(buildInitialSelections(templates, initialSelections));
      setPresetsMap({});
      fetchedTemplates.current = new Set();
      setLoadingId(firstId);
      setResetKey((k) => k + 1);
    }
  }, [show]);

  const fetchPresetsForTemplate = useCallback(
    async (templateId) => {
      if (!templateId || fetchedTemplates.current.has(templateId)) return;
      fetchedTemplates.current.add(templateId);
      setLoadingId(templateId);
      try {
        const results = await dispatch(getCameraDataFromApi(templateId, 'room_id'));
        setPresetsMap((prev) => ({ ...prev, [templateId]: results || [] }));
      } catch {
        fetchedTemplates.current.delete(templateId);
        setPresetsMap((prev) => ({ ...prev, [templateId]: [] }));
      } finally {
        setLoadingId(null);
      }
    },
    [dispatch]
  );

  useEffect(() => {
    if (activeTemplateId) fetchPresetsForTemplate(activeTemplateId);
  }, [activeTemplateId, resetKey]);

  const presets = presetsMap[activeTemplateId] || [];
  const presetsLoading = loadingId === activeTemplateId || (!!activeTemplateId && !(activeTemplateId in presetsMap));

  const currentSelected = selections[activeTemplateId] || new Set();
  const countFor = (templateId) => selections[templateId]?.size || 0;

  const togglePreset = (presetId) => {
    setSelections((prev) => {
      const curr = new Set(prev[activeTemplateId] || []);
      curr.has(presetId) ? curr.delete(presetId) : curr.add(presetId);
      return { ...prev, [activeTemplateId]: curr };
    });
  };

  const selectAllPresets = () => {
    setSelections((prev) => ({
      ...prev,
      [activeTemplateId]: new Set(presets.map((p) => p.id)),
    }));
  };

  const clearPresets = () => {
    setSelections((prev) => ({ ...prev, [activeTemplateId]: new Set() }));
  };

const allTemplatesHavePresets =
    templates.length > 0 && templates.every((t) => (selections[t.id]?.size || 0) > 0);

  const handleSave = () => {
    if (!allTemplatesHavePresets) {
      warningToast('Please select at least one camera angle for each template.');
      return;
    }
    const result = {};
    Object.entries(selections).forEach(([templateId, selectedIds]) => {
      const templatePresets = presetsMap[templateId] || [];
      result[templateId] = {
        presetIds: Array.from(selectedIds),
        presets: templatePresets.filter((p) => selectedIds.has(p.id)),
      };
    });
    onSave?.(result);
    onClose?.();
  };

  const activeTemplate = templates.find((t) => t.id === activeTemplateId);
  const activeCount = countFor(activeTemplateId);

  const body = (
    <div>
      <p className={styles.description}>
        Choose which camera presets should have dimension lines rendered. Renders will be generated
        automatically for each selected camera preset.
      </p>

      {templates.length > 1 && (
        <div className={styles.templateTabs}>
          {templates.map((tpl, i) => {
            const count = countFor(tpl.id);
            const isActive = tpl.id === activeTemplateId;
            const dotColor = isActive
              ? 'var(--white-color)'
              : count === 0
                ? 'var(--primary-color)'
                : 'var(--render-done)';
            const fullName = tpl.name || tpl.title || `Template ${i + 1}`;
            return (
              <Button
                key={tpl.id}
                color="link"
                className={`${styles.templateTab} ${isActive ? styles.active : ''}`}
                onClick={() => setActiveTemplateId(tpl.id)}>
                <span className={styles.tabDot} style={{ background: dotColor }} />
                <span className={styles.tabLabel} title={fullName}>
                  {fullName}
                </span>
                {count > 0 && <span className={styles.tabCount}>({count})</span>}
              </Button>
            );
          })}
        </div>
      )}

      <div className={styles.presetsSectionHeader}>
        <p className={styles.presetsSectionTitle}>
          <span className={styles.presetsSectionLabel}>Camera Presets</span>
          {activeTemplate && (
            <span className={styles.presetsSectionTemplateName} title={activeTemplate.name || activeTemplate.title || ''}>
              {'\u00A0—\u00A0'}
              {activeTemplate.name || activeTemplate.title || ''}
            </span>
          )}
        </p>
        {presets.length > 0 && (
          <div className={styles.presetsActions}>
            <Button color="link" smallPadding onClick={selectAllPresets}>
              Select all
            </Button>
            <Button color="link" smallPadding onClick={clearPresets}>
              Clear
            </Button>
          </div>
        )}
      </div>

      {presetsLoading ? (
        <Loader loading position="relative" style={{ padding: '60px 0' }} />
      ) : presets.length === 0 ? (
        <p className={styles.stateMsg}>No camera presets found</p>
      ) : (
        <div className={styles.presetsGrid}>
          {presets.map((preset) => {
            const isSelected = currentSelected.has(preset.id);
            const resolution =
              preset.resoX && preset.resoY ? `${preset.resoX}×${preset.resoY}` : null;
            const presetName = preset.cameraName || preset.name;
            return (
              <div
                key={preset.id}
                className={`${styles.presetCard} ${isSelected ? styles.selected : ''}`}
                onClick={() => togglePreset(preset.id)}>
                <div onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    selected={isSelected}
                    onChange={() => togglePreset(preset.id)}
                    name={`preset-${preset.id}`}
                    isLowerCase={false}
                  />
                </div>
                <div className={styles.presetInfo}>
                  <p className={styles.presetName} title={presetName}>
                    {presetName}
                  </p>
                  {resolution && <p className={styles.presetMeta}>{resolution}</p>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!!activeCount && !presetsLoading && (
        <div className={styles.countBar}>
          <span className={styles.countText}>
            <strong>{activeCount}</strong>
            <span>
              camera preset{activeCount !== 1 ? 's' : ''} selected
              {templates.length > 1 ? ' for this template' : ''}
            </span>
          </span>
        </div>
      )}

      <div className={styles.footer}>
        <Button variant="outline" size="xs" color="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button color="primary" size="xs" onClick={handleSave} disabled={!allTemplatesHavePresets || !!loadingId}>
          Save
        </Button>
      </div>
    </div>
  );

  return (
    <ModalArea
      showModal={show}
      setShowModal={(val) => {
        if (!val) onClose?.();
      }}
      title="Select Camera Presets"
      headingLeft
      body={body}
      size="lg"
      centered
      backdropClassName="overlay-dark camera-presets-backdrop"
      style={{ zIndex: 1060 }}
    />
  );
}
